/* eslint-disable */
// AIレポート機能のAPIクライアント。
// モック/実APIは VUE_APP_AI_REPORT_USE_MOCK で切替可能。
// 型定義は chaild-api の nursery aiReport types に合わせている。型が npm 公開され次第、
// ローカル型定義を `chaild-api/lib` (prj__chaild__api) からの import に差し替える。

import ApiClient from "@/api/ApiClient";

// =====================================================================
// 型定義 (chaild-api/lib に切り出し予定)
// =====================================================================

export type AiReportStatus = "notGenerated" | "draft" | "published";
export type AiReportBatchStatus = "processing" | "completed" | "failed";
export type AiReportBatchKind =
  | "bulkGenerate"
  | "individualRegenerate"
  | "bulkPublish";
export type AiReportQuarter = 1 | 2 | 3 | 4;

// 再生成「翻訳」モードで指定できる言語コード
export type AiReportLanguage =
  | "en"
  | "ja"
  | "ko"
  | "zh"
  | "vi"
  | "km"
  | "hi"
  | "ru"
  | "bn"
  | "mn"
  | "tl"
  | "ms";

// 翻訳先言語の選択肢 (表示順)
export const AI_REPORT_LANGUAGES: { code: AiReportLanguage; label: string }[] = [
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" },
  { code: "vi", label: "Vietnamese" },
  { code: "km", label: "Khmer" },
  { code: "hi", label: "Hindi" },
  { code: "ru", label: "Russian" },
  { code: "bn", label: "Bengali" },
  { code: "mn", label: "Mongolian" },
  { code: "tl", label: "Tagalog" },
  { code: "ms", label: "Malay" },
];

export interface AiReportBatchResponse {
  batchId: number;
  nurseryId: number;
  businessYear: number;
  quarter: AiReportQuarter;
  kind: AiReportBatchKind;
  status: AiReportBatchStatus;
  totalCount: number;
  successCount: number;
  failedCount: number;
  startedAt: string;
  finishedAt: string | null;
  triggeredBy: { userId: number; displayName: string } | null;
}

export interface AiReportQuarterSummary {
  quarter: AiReportQuarter;
  // Month range label (e.g. "8-10")
  monthsLabel: string;
  totalChildren: number;
  notGeneratedCount: number;
  draftCount: number;
  publishedCount: number;
  latestBatch: AiReportBatchResponse | null;
}

export interface AiReportTopResponse {
  businessYear: number;
  quarters: AiReportQuarterSummary[];
}

export interface AiReportChildRow {
  childId: number;
  lastName: string;
  firstName: string;
  className: string;
  ageLabel: string;
  status: AiReportStatus;
  generationCount: number;
  // 生成に使えた発達記録の対象月集合 (保護者注意書き判定用)
  dataCoverageMonths: number[];
  // クォーター内で「できた(⚪︎)」が付いた発達項目の数
  achievedItemCount: number;
  // クォーター内で経過記録 (content有) が入力されている月の数
  progressRecordCount: number;
  lastGeneratedAt: string | null;
  lastGeneratedBy: string | null;
  lastPublishedAt: string | null;
  lastPublishedBy: string | null;
}

export interface AiReportClassGroup {
  classId: number;
  className: string;
  ageLabel: string;
  children: AiReportChildRow[];
}

export interface AiReportQuarterDetailResponse {
  businessYear: number;
  quarter: AiReportQuarter;
  monthsLabel: string;
  classes: AiReportClassGroup[];
  latestBatch: AiReportBatchResponse | null;
}

export interface AiReportDetailResponse {
  childId: number;
  lastName: string;
  firstName: string;
  className: string;
  ageLabel: string;
  businessYear: number;
  quarter: AiReportQuarter;
  monthsLabel: string;
  status: AiReportStatus;
  content: string | null;
  additionalPrompt: string | null;
  dataCoverageMonths: number[];
  generationCount: number;
  lastGeneratedAt: string | null;
  lastGeneratedBy: string | null;
  lastPublishedAt: string | null;
  lastPublishedBy: string | null;
}

// =====================================================================
// モックデータ
// =====================================================================

// モック環境フラグ: VUE_APP_AI_REPORT_USE_MOCK='true' を設定した環境でのみモック応答に切り替える。
// 未設定時はデフォルトで実APIに疎通する（ローカルでオフライン確認したい場合のみ明示的に true を設定）。
const USE_MOCK = process.env.VUE_APP_AI_REPORT_USE_MOCK === 'true';

const MOCK_BASE_BUSINESS_YEAR = 2026;

const MOCK_CONTENT_SAMPLE = `Hello, I am VEVO, the childcare robot.
Akira showed remarkable growth over these three months, especially in physical play and friendships.
In jump rope, consecutive jumps became possible, and core strength improved enough to stand on one foot for over 10 seconds. In tag and dodgeball, Akira often explained the rules and helped with team selection, showing leadership.
Cognitively, Akira can plan ahead when solving complex puzzles. Observation skills also grew—small seasonal changes are noticed and shared with friends and teachers.
Socially, feelings such as frustration and joy are gradually expressed in words, with better emotional control during group play.
Akira's curiosity, focus, and cheerful energy will continue to be great strengths. We will keep supporting physical and creative play while helping expand emotional expression.

Today again / laughing with friends / winter wind`;

const MOCK_QUARTERS: { quarter: AiReportQuarter; monthsLabel: string }[] = [
  { quarter: 1, monthsLabel: "8-10" },
  { quarter: 2, monthsLabel: "11-1" },
  { quarter: 3, monthsLabel: "2-4" },
  { quarter: 4, monthsLabel: "5-7" },
];

interface MockChild {
  childId: number;
  lastName: string;
  firstName: string;
  classId: number;
  className: string;
  ageLabel: string;
}

const MOCK_CHILDREN: MockChild[] = [
  { childId: 101, lastName: "Yamada", firstName: "Akira", classId: 1, className: "Elephant", ageLabel: "5-year-olds" },
  { childId: 102, lastName: "Tanaka", firstName: "Haruka", classId: 1, className: "Elephant", ageLabel: "5-year-olds" },
  { childId: 103, lastName: "Suzuki", firstName: "Sota", classId: 1, className: "Elephant", ageLabel: "5-year-olds" },
  { childId: 201, lastName: "Sato", firstName: "Mio", classId: 2, className: "Giraffe", ageLabel: "4-year-olds" },
  { childId: 202, lastName: "Takahashi", firstName: "Yuto", classId: 2, className: "Giraffe", ageLabel: "4-year-olds" },
  { childId: 301, lastName: "Nakamura", firstName: "Hiyori", classId: 3, className: "Rabbit", ageLabel: "3-year-olds" },
  { childId: 302, lastName: "Kobayashi", firstName: "Kento", classId: 3, className: "Rabbit", ageLabel: "3-year-olds" },
];

// 状態は (businessYear, quarter, childId) で一意。クォーター×園児ごとに固定パターンを返す。
function mockStatusFor(quarter: AiReportQuarter, childId: number): AiReportStatus {
  // 状態のばらつきをUIで確認できるよう、決定的にミックスする
  const mod = (quarter * 7 + childId) % 6;
  if (mod === 0) return "published";
  if (mod === 1) return "published";
  if (mod === 2) return "draft";
  if (mod === 3) return "draft";
  if (mod === 4) return "notGenerated";
  return "notGenerated";
}

function mockCoverageMonths(quarter: AiReportQuarter, childId: number): number[] {
  const quarterMonths: Record<AiReportQuarter, number[]> = {
    1: [8, 9, 10],
    2: [11, 12, 1],
    3: [2, 3, 4],
    4: [5, 6, 7],
  };
  const months = quarterMonths[quarter];
  // Some children intentionally have partial coverage for UI testing
  if (childId % 5 === 0) return [months[0]];
  if (childId % 7 === 0) return [months[0], months[1]];
  return months;
}

// 一覧用モック: 達成項目数 (0〜5個を決定的に返す)
function mockAchievedItemCount(quarter: AiReportQuarter, childId: number): number {
  return (childId * 2 + quarter) % 6;
}

function buildBatchSummary(
  businessYear: number,
  quarter: AiReportQuarter,
  status: AiReportBatchStatus,
  kind: AiReportBatchKind = "bulkGenerate",
  overrides: Partial<AiReportBatchResponse> = {},
): AiReportBatchResponse {
  const total = MOCK_CHILDREN.length;
  return {
    batchId: 9000 + quarter * 10,
    nurseryId: 1,
    businessYear,
    quarter,
    kind,
    status,
    totalCount: total,
    successCount: status === "completed" ? total : status === "processing" ? Math.floor(total / 2) : 3,
    failedCount: status === "failed" ? 1 : 0,
    startedAt: new Date(Date.now() - 60_000 * 3).toISOString(),
    finishedAt: status === "processing" ? null : new Date().toISOString(),
    triggeredBy: { userId: 1, displayName: "Test Lead" },
    ...overrides,
  };
}

function buildTopSummary(businessYear: number): AiReportTopResponse {
  const quarters = MOCK_QUARTERS.map<AiReportQuarterSummary>(({ quarter, monthsLabel }) => {
    const counts = MOCK_CHILDREN.reduce(
      (acc, c) => {
        const st = mockStatusFor(quarter, c.childId);
        if (st === "notGenerated") acc.notGeneratedCount++;
        if (st === "draft") acc.draftCount++;
        if (st === "published") acc.publishedCount++;
        return acc;
      },
      { notGeneratedCount: 0, draftCount: 0, publishedCount: 0 },
    );
    return {
      quarter,
      monthsLabel,
      totalChildren: MOCK_CHILDREN.length,
      ...counts,
      latestBatch:
        quarter === 3
          ? buildBatchSummary(businessYear, quarter, "completed")
          : quarter === 4
          ? null
          : buildBatchSummary(businessYear, quarter, "completed"),
    };
  });
  return { businessYear, quarters };
}

function buildQuarterDetail(
  businessYear: number,
  quarter: AiReportQuarter,
): AiReportQuarterDetailResponse {
  const monthsLabel = MOCK_QUARTERS.find((q) => q.quarter === quarter)?.monthsLabel ?? "";

  const classMap = new Map<number, AiReportClassGroup>();
  for (const c of MOCK_CHILDREN) {
    if (!classMap.has(c.classId)) {
      classMap.set(c.classId, {
        classId: c.classId,
        className: c.className,
        ageLabel: c.ageLabel,
        children: [],
      });
    }
    const status = mockStatusFor(quarter, c.childId);
    classMap.get(c.classId)!.children.push({
      childId: c.childId,
      lastName: c.lastName,
      firstName: c.firstName,
      className: c.className,
      ageLabel: c.ageLabel,
      status,
      generationCount: status === "notGenerated" ? 0 : ((c.childId + quarter) % 3) + 1,
      dataCoverageMonths: mockCoverageMonths(quarter, c.childId),
      achievedItemCount: mockAchievedItemCount(quarter, c.childId),
      progressRecordCount: mockCoverageMonths(quarter, c.childId).length,
      lastGeneratedAt: status === "notGenerated" ? null : new Date(Date.now() - 86400_000 * 2).toISOString(),
      lastGeneratedBy: status === "notGenerated" ? null : "Test Lead",
      lastPublishedAt: status === "published" ? new Date(Date.now() - 86400_000).toISOString() : null,
      lastPublishedBy: status === "published" ? "Test Lead" : null,
    });
  }

  return {
    businessYear,
    quarter,
    monthsLabel,
    classes: Array.from(classMap.values()),
    latestBatch: quarter === 3 ? buildBatchSummary(businessYear, quarter, "completed") : null,
  };
}

function buildDetail(
  businessYear: number,
  quarter: AiReportQuarter,
  childId: number,
): AiReportDetailResponse | null {
  const child = MOCK_CHILDREN.find((c) => c.childId === childId);
  if (!child) return null;
  const status = mockStatusFor(quarter, childId);
  const monthsLabel = MOCK_QUARTERS.find((q) => q.quarter === quarter)?.monthsLabel ?? "";
  return {
    childId,
    lastName: child.lastName,
    firstName: child.firstName,
    className: child.className,
    ageLabel: child.ageLabel,
    businessYear,
    quarter,
    monthsLabel,
    status,
    content: status === "notGenerated" ? null : MOCK_CONTENT_SAMPLE,
    additionalPrompt: status === "draft" ? null : null,
    dataCoverageMonths: mockCoverageMonths(quarter, childId),
    generationCount: status === "notGenerated" ? 0 : ((childId + quarter) % 3) + 1,
    lastGeneratedAt: status === "notGenerated" ? null : new Date(Date.now() - 86400_000 * 2).toISOString(),
    lastGeneratedBy: status === "notGenerated" ? null : "Test Lead",
    lastPublishedAt: status === "published" ? new Date(Date.now() - 86400_000).toISOString() : null,
    lastPublishedBy: status === "published" ? "Test Lead" : null,
  };
}

// 進捗ポーリング用の擬似ステート。バッチID単位で経過秒数に応じて success_count を増やす。
const mockBatchStartedAt = new Map<number, number>();
let mockBatchSeq = 10000;

function buildProgressBatch(batchId: number): AiReportBatchResponse {
  const startedAt = mockBatchStartedAt.get(batchId) ?? Date.now();
  const elapsedMs = Date.now() - startedAt;
  const total = MOCK_CHILDREN.length;
  // 1件あたり 1.2 秒で完了する想定でシミュレート
  const completed = Math.min(total, Math.floor(elapsedMs / 1200));
  const status: AiReportBatchStatus = completed >= total ? "completed" : "processing";
  return {
    batchId,
    nurseryId: 1,
    businessYear: MOCK_BASE_BUSINESS_YEAR,
    quarter: 3,
    kind: "bulkGenerate",
    status,
    totalCount: total,
    successCount: completed,
    failedCount: 0,
    startedAt: new Date(startedAt).toISOString(),
    finishedAt: status === "completed" ? new Date(startedAt + total * 1200).toISOString() : null,
    triggeredBy: { userId: 1, displayName: "Test Lead" },
  };
}

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// =====================================================================
// APIクライアント
// =====================================================================

export default class ApiAiReport {
  static async getTop({
    nurseryId,
    businessYear,
  }: {
    nurseryId: number;
    businessYear: number;
  }): Promise<AiReportTopResponse> {
    if (USE_MOCK) {
      return delay(buildTopSummary(businessYear));
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/summary`,
      null,
    );
    return response as AiReportTopResponse;
  }

  static async getQuarterDetail({
    nurseryId,
    businessYear,
    quarter,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<AiReportQuarterDetailResponse> {
    if (USE_MOCK) {
      return delay(buildQuarterDetail(businessYear, quarter));
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}`,
      null,
    );
    return response as AiReportQuarterDetailResponse;
  }

  static async getDetail({
    nurseryId,
    businessYear,
    quarter,
    childId,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
  }): Promise<AiReportDetailResponse | null> {
    if (USE_MOCK) {
      return delay(buildDetail(businessYear, quarter, childId));
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}`,
      null,
    );
    return response as AiReportDetailResponse;
  }

  static async bulkGenerate({
    nurseryId,
    businessYear,
    quarter,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<AiReportBatchResponse> {
    if (USE_MOCK) {
      const batchId = ++mockBatchSeq;
      mockBatchStartedAt.set(batchId, Date.now());
      return delay(
        {
          ...buildProgressBatch(batchId),
          businessYear,
          quarter,
        },
        400,
      );
    }
    const response = await ApiClient.post(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/generate`,
      null,
    );
    return response as AiReportBatchResponse;
  }

  static async regenerateChild({
    nurseryId,
    businessYear,
    quarter,
    childId,
    language,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    // 指定時は「翻訳」モード。未指定は単純な再生成。
    language?: AiReportLanguage | null;
  }): Promise<AiReportBatchResponse> {
    if (USE_MOCK) {
      const batchId = ++mockBatchSeq;
      mockBatchStartedAt.set(batchId, Date.now());
      return delay(
        {
          ...buildProgressBatch(batchId),
          businessYear,
          quarter,
          kind: "individualRegenerate",
          totalCount: 1,
          successCount: 0,
        },
        400,
      );
    }
    const response = await ApiClient.post(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}/regenerate`,
      { language: language ?? null },
    );
    return response as AiReportBatchResponse;
  }

  static async bulkPublish({
    nurseryId,
    businessYear,
    quarter,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<{ publishedCount: number }> {
    if (USE_MOCK) {
      return delay({ publishedCount: MOCK_CHILDREN.length }, 400);
    }
    const response = await ApiClient.post(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/publish`,
      null,
    );
    return response as { publishedCount: number };
  }

  static async setStatus({
    nurseryId,
    businessYear,
    quarter,
    childId,
    status,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    status: "published" | "draft";
  }): Promise<AiReportDetailResponse | null> {
    if (USE_MOCK) {
      const detail = buildDetail(businessYear, quarter, childId);
      if (!detail) return delay(null, 200);
      const next: AiReportDetailResponse = {
        ...detail,
        status,
        lastPublishedAt:
          status === "published" ? new Date().toISOString() : detail.lastPublishedAt,
        lastPublishedBy: status === "published" ? "Test Lead" : detail.lastPublishedBy,
      };
      return delay(next, 250);
    }
    const response = await ApiClient.put(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}/status`,
      { status },
    );
    return response as AiReportDetailResponse;
  }

  static async getBatch({
    nurseryId,
    batchId,
  }: {
    nurseryId: number;
    batchId: number;
  }): Promise<AiReportBatchResponse> {
    if (USE_MOCK) {
      return delay(buildProgressBatch(batchId), 150);
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/batches/${batchId}`,
      null,
    );
    return response as AiReportBatchResponse;
  }

  static async exportCsv({
    nurseryId,
    businessYear,
    quarter,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<{ accepted: true }> {
    if (USE_MOCK) {
      return delay({ accepted: true }, 350);
    }
    const response = await ApiClient.post(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/export`,
      null,
    );
    return response as { accepted: true };
  }
}
